import { useState } from "react";
import { Button, Icon, Loading, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import {
  clientPaymentApi,
  useDeleteClientPaymentMethodMutation,
  useGetClientPaymentMethodsQuery,
  useSetDefaultPaymentMethodMutation,
} from "@/redux/apis/clientPayment/clientPaymentApi";
import { ClientPaymentMethod, PaymentMethodType } from "@/redux/apis/clientPayment/types";
import { useGetClientProfileQuery } from "@/redux/apis/clientProfile/clientProfileApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { accountIdSelector } from "@/redux/slices/account/selectors";
import { configSelector } from "@/redux/slices/config/selectors";
import { addToast } from "@/utils";
import { AddCardIntroModal } from "./components/AddCardIntroModal";
import { CardFormModal } from "./components/CardFormModal";
import { CardRow } from "./components/CardRow";
import { CardSecurityTooltip } from "./components/CardSecurityTooltip";
import { DeleteCardModal } from "./components/DeleteCardModal";
import { GuidanceNotice } from "./components/GuidanceNotice";
import { InvoiceBillingCard } from "./components/InvoiceBillingCard";
import { isCreditCard, MAX_CARDS_PER_ACCOUNT } from "./helpers";

type ActiveModal =
  | { type: "add-intro" }
  | { type: "add" }
  | { type: "edit"; card: ClientPaymentMethod }
  | { type: "delete"; card: ClientPaymentMethod }
  | null;

/**
 * Every dialog here is a vaul Drawer on mobile, and two root drawers transitioning in the same
 * tick cancel each other (the opening one closes immediately). Swapping one dialog for another
 * therefore closes the first and opens the second after its close animation, so only ever one
 * is transitioning.
 */
const DRAWER_SWAP_MS = 350;

export const PaymentMethodsCard = ({ accountId: managedAccountId }: { accountId?: string }) => {
  const dispatch = useAppDispatch();
  const selfAccountId = useAppSelector(accountIdSelector);
  const accountId = managedAccountId ?? selfAccountId;
  // Shared card: operator-managed view uses 3rd-person copy, client self-service keeps 2nd-person.
  const isOperatorView = !!managedAccountId;

  const { data: paymentMethods = [], isLoading, error, refetch } = useGetClientPaymentMethodsQuery(accountId, { skip: !accountId });
  const [deletePaymentMethod, { isLoading: isDeleting }] = useDeleteClientPaymentMethodMutation();
  const [setDefaultPaymentMethod, { isLoading: isSettingDefault }] = useSetDefaultPaymentMethodMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);

  const { data: clientProfile } = useGetClientProfileQuery(managedAccountId ? { accountId: managedAccountId } : undefined);
  const operatorName = useAppSelector(configSelector)?.name;

  const cards = paymentMethods.filter(isCreditCard);
  const invoiceMethod = paymentMethods.find((m) => m.type === PaymentMethodType.INVOICE);
  const companyName = clientProfile?.company?.company_name;
  // Gate add-card solely on count — card type is now auto-assigned server-side (DEV-1142).
  // The backend caps active cards at MAX_CARDS_PER_ACCOUNT.
  const canAddCard = cards.length < MAX_CARDS_PER_ACCOUNT;

  const closeModal = () => setActiveModal(null);

  const swapModal = (next: ActiveModal) => {
    closeModal();
    setTimeout(() => setActiveModal(next), DRAWER_SWAP_MS);
  };

  const requestDelete = (cardToDelete: ClientPaymentMethod) => swapModal({ type: "delete", card: cardToDelete });

  const refreshList = () => dispatch(clientPaymentApi.util.invalidateTags(["ClientPaymentMethods"]));

  const handleSaved = async (newPaymentMethodId: string, replacedCard?: ClientPaymentMethod, isRename?: boolean) => {
    if (isRename) {
      addToast("success", "Credit card updated");
    } else if (replacedCard) {
      // replacement id lets the backend repoint the old card's unpaid jobs so in-use cards can be swapped
      await deletePaymentMethod({ accountId, paymentMethodId: replacedCard.uuid, replacementId: newPaymentMethodId })
        .unwrap()
        .then(() => addToast("success", "Credit card updated"))
        .catch((e) => {
          getErrorMessages(e).forEach((message) => addToast("danger", message));
          addToast("danger", "The new card was added but the old one could not be removed");
        });
    } else {
      addToast("success", "Successfully added new credit card");
    }
    refreshList();
    closeModal();
  };

  const handleDelete = () => {
    if (activeModal?.type !== "delete") return;
    deletePaymentMethod({ accountId, paymentMethodId: activeModal.card.uuid })
      .unwrap()
      .then(() => {
        addToast("success", "Credit card removed");
        closeModal();
      })
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  const handleSetDefault = (card: ClientPaymentMethod) => {
    setDefaultPaymentMethod({ accountId, paymentMethodId: card.uuid })
      .unwrap()
      .then(() => addToast("success", "Default payment method updated"))
      .catch((e) => getErrorMessages(e).forEach((message) => addToast("danger", message)));
  };

  const renderAddCardTile = () => (
    <button
      type="button"
      className="flex h-[52px] w-full items-center gap-3.5 rounded-lg border border-dashed border-info bg-info-light px-3.5 hover:bg-info-light/70"
      onClick={() => setActiveModal(cards.length > 0 ? { type: "add-intro" } : { type: "add" })}
    >
      <Icon name="Add" size={24} className="text-info" />
      <Typography variant="action" className="text-info-dark">
        Add credit card
      </Typography>
    </button>
  );

  const renderContent = () => {
    if (isLoading) return <Loading className="relative min-h-[120px] !bg-transparent" />;

    if (error)
      return (
        <div className="flex flex-col items-start gap-2">
          <Typography variant="paragraph" className="text-danger">
            {getErrorMessages(error)[0] ?? "Unable to load your payment methods."}
          </Typography>
          <Button variant="secondary" size="sm" onClick={refetch}>
            Try again
          </Button>
        </div>
      );

    return (
      <div className="flex flex-col gap-4">
        {invoiceMethod && <InvoiceBillingCard companyName={companyName} operatorName={operatorName} isDefault={!!invoiceMethod.default} />}

        {/* Invoice-billed clients have a usable payment method without a card, so this isn't their warning. */}
        {cards.length === 0 && !invoiceMethod && (
          <div className="flex items-center gap-2 rounded-lg border border-warning bg-warning-light py-1 pl-2 pr-4">
            <Icon name="Danger" variant="Bold" size={20} className="shrink-0 text-warning" />
            <div className="flex flex-col">
              <Typography variant="action" className="text-warning-dark">
                No Payment Method Selected
              </Typography>
              <Typography variant="small-regular" className="!font-medium text-warning-dark">
                Please add a credit card, or select Pay by Invoice if it&apos;s available for your account.
              </Typography>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Icon name="Card" size={20} className="text-neutral-black" />
          <Typography variant="action" className="text-neutral-black">
            Credit Card Payments
          </Typography>
        </div>
        <CardSecurityTooltip />

        {cards.length >= 2 && (
          <GuidanceNotice tone="warning" icon={{ name: "LampCharge", variant: "Bold" }} title="Good to know before you book">
            {isOperatorView
              ? "When the client books, double-check the card selected is the right one. Payments made to the wrong card can't be reversed."
              : "When you book, double-check the card you've selected is the right one. Payments made to the wrong card can't be reversed."}
          </GuidanceNotice>
        )}

        {/* Shown in every state, empty included: the rule needs to be known before the first card is added. */}
        <GuidanceNotice tone="info" icon={{ name: "help-circle", isCustom: true }} title="Replacing a card?">
          If a card is already being used for existing bookings, update that card instead of adding a new one. Adding a new card or setting
          it as default will only apply to new bookings.
        </GuidanceNotice>

        {cards.map((card) => (
          <CardRow
            key={card.uuid}
            method={card}
            isEditing={isEditing}
            isSettingDefault={isSettingDefault}
            lockedByInvoice={!!invoiceMethod}
            onEditCard={(c) => setActiveModal({ type: "edit", card: c })}
            onSetDefault={handleSetDefault}
          />
        ))}

        {(isEditing || cards.length === 0) && canAddCard && renderAddCardTile()}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-5 rounded-lg border border-neutral-mid-gray bg-white p-5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Typography variant="title" className="text-neutral-silver-gray">
            Payment Methods
          </Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            {isOperatorView
              ? "Choose the method to use by default. The client can switch methods for individual trips at checkout."
              : "Choose the method we'll use by default. You can switch methods for individual trips at checkout."}
          </Typography>
        </div>
        {!isLoading && !error && cards.length > 0 && (
          <div className="flex shrink-0 items-center gap-2">
            {isEditing ? (
              <>
                <Button variant="secondary" size="sm" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" onClick={() => setIsEditing(false)}>
                  Save
                </Button>
              </>
            ) : (
              <Button variant="secondary" size="sm" endIcon="Edit2" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            )}
          </div>
        )}
      </div>

      {renderContent()}

      <AddCardIntroModal open={activeModal?.type === "add-intro"} onClose={closeModal} onContinue={() => swapModal({ type: "add" })} />

      <CardFormModal
        open={activeModal?.type === "add" || activeModal?.type === "edit"}
        mode={activeModal?.type === "edit" ? "edit" : "add"}
        accountId={accountId}
        card={activeModal?.type === "edit" ? activeModal.card : undefined}
        onClose={closeModal}
        onSaved={handleSaved}
        onRequestDelete={requestDelete}
      />

      <DeleteCardModal open={activeModal?.type === "delete"} isDeleting={isDeleting} onClose={closeModal} onConfirm={handleDelete} />
    </div>
  );
};
